import { useCallback, useEffect, useMemo, useState } from 'react';

import { IllustCompleted, IllustWarning } from '@common/icons';

import { OverlayCompProps } from '../common/util/createOverlayComp';
import { BottomDialog } from './dump/BottomDialog';
import { ConfirmReturnValue, DialogMessage, DialogMessageObject } from './Dialog';

function RecordConfirm({
  message,
  cancel,
  ok,
  title,
  type,
  resolve,
  dispose,
}: DialogMessageObject & OverlayCompProps<ConfirmReturnValue>) {
  const [active, setActive] = useState(true);
  const proceed = useCallback(
    (value: ConfirmReturnValue) => {
      resolve?.(value);
      setActive(false);
      dispose?.();
    },
    [resolve, setActive, dispose],
  );
  const image = useMemo(() => {
    if (type === 'warning') {
      return <IllustWarning />;
    }
    if (type === 'complete') {
      return <IllustCompleted />;
    }
    return undefined;
  }, [type]);
  const handleOutsideClick = useCallback(() => {
    proceed('skip');
  }, [proceed]);

  useEffect(() => {
    return () => {
      dispose?.();
    };
  }, [dispose]);

  return (
    <BottomDialog
      active={active}
      image={image}
      title={title}
      text={message}
      handleOutsideClick={handleOutsideClick}
      buttons={[
        {
          id: 'cancel',
          children: cancel || '취소',
          onClick: () => {
            proceed('cancel');
          },
          variant: 'default',
          size: 'lg',
        },
        {
          id: 'ok',
          children: ok || '확인',
          onClick: () => {
            proceed('ok');
          },
          size: 'lg',
        },
      ]}
    />
  );
}

export function renderConfirm(message: DialogMessage) {
  return ({ resolve, dispose }: OverlayCompProps<ConfirmReturnValue>) => {
    if (typeof message === 'object') {
      return <RecordConfirm {...{ ...message, resolve, dispose }} />;
    }
    return <RecordConfirm {...{ message, resolve, dispose }} />;
  };
}
